import Input from 'components/Input';
import Button from 'components/Button';
import InlineTitle from 'components/InlineTitle';
import DropdownWithItem from 'components/DropdownWithItem';
import DropdownWithAdvancedSearch from 'components/DropdownWithAdvancedSearch';

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";
import { isNil, size, cloneDeep } from 'lodash';

import { jsonToCsv } from "utils/downloadFile.util";
import Nuclias from "const/nuclias";

import useConst, { defaultColumns } from "../../useConst";

const { timeFormat, licenseConfig, deviceType } = Nuclias;
const searchAction = {
  fuzzy: 'fuzzy',
  option: 'option',
};

const defaultSearch = {
  searchKey: '',
  searchFields: {},
  type: searchAction.fuzzy,
};

const LicensesToolbarBoxORG = ({
  dataList,
  handleSearch,
  quickSearchKey
}) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filterOptions, resetFilterOptions, changeOption, changeMultiOption } = useConst();
  const [search, setSearch] = useState(defaultSearch);

  const initOrgSearch = () => {
    setSearch(search => (defaultSearch));
    resetFilterOptions();
  };

  const initSearchKey = () => {
    if (size(search?.searchFields?.key?.value) > 0) {
      const copy = cloneDeep(search);
      copy.searchFields.key.value = '';
      setSearch(search => (copy));
    }
  };

  const disableFuzzySearch = () => {
    let defaultOptionSearch = cloneDeep(defaultSearch);
    defaultOptionSearch.type = searchAction.option;
    setSearch(search => (defaultOptionSearch));
  };

  const getFilterList = (type) => {
    return filterOptions[type].data;
  };

  const getSelectedFilter = (type) => {
    for (const [index, item] of filterOptions[type].data.entries()) {
      if (item.isActive)
        return item;
    }
  };

  const setSrarchText = (fields, searchObj) => {
    if (isNil(searchObj)) {
      searchObj = cloneDeep(defaultSearch);
    }
    searchObj.type = searchAction.option;
    for (const [fieldIndex, field] of fields.entries()) {
      field.searchType = filterOptions[field.type].searchType;
      field.name = filterOptions[field.type].name;
      searchObj.searchFields[field.type] = field;
    }

    const conditions = [];
    for (const [key, item] of Object.entries(searchObj.searchFields)) {
      if (item.value !== null && item.value !== '') {
        if (key !== 'licenseKey')
          conditions.push(`(${t(item.name)}: ${t(item.title)})`);
        else
          conditions.push(`(${t(item.name)}: ${t(item.value)})`);
      }
    };
    const conditionsStr = conditions.join(' AND ');
    searchObj.searchKey = conditionsStr;
    setSearch(search => (searchObj));
  };

  const changeMultiSearchOption = (list) => {
    const result = changeMultiOption(list);
    setSrarchText(result);
  };

  const changeSearchOption = (target, type, isQuick) => {
    if (target?.type === 'change') {
      target = { title: '', value: target.target.value, isActive: true };
    } else {
      target = changeOption(target, type);
    }
    let field = cloneDeep(target);
    field.type = type;
    let obj = cloneDeep(search);
    if (isQuick) { obj = null; }
    setSrarchText([field], obj);
  };

  const downloadORGCsv = useCallback(() => {
    const columns = defaultColumns();
    let fields = [];
    let headers = [];
    for (const [colIndex, col] of columns.entries()) {
      headers.push(col.label); // or t(col.label)
      fields.push(col.downloadName || col.accessor);
    }
    const fileName = `${'ORG_License'}_${dayjs().format(timeFormat.csvDownloadFormat)}.csv`;
    jsonToCsv(fileName, headers, fields, dataList);
  }, [dataList]);

  const inputQuickSearch = (key) => {
    switch (key.value) {
      case 'expireIn60days':
        changeSearchOption({ value: 60 }, 'timeRemaining', true);
        break;
      case 'used':
        changeSearchOption({ value: licenseConfig.usedTypes.used }, 'licenseType', true);
        break;
      case 'unusedInactive':
        const uilist = [
          { value: licenseConfig.usedTypes.unused, type: 'licenseType' },
          { value: licenseConfig.statusTypes.inactive, type: 'status' }
        ];
        changeMultiSearchOption(uilist);
        break;
      case 'unusedActive':
        const ualist = [
          { value: licenseConfig.usedTypes.unused, type: 'licenseType' },
          { value: licenseConfig.statusTypes.active, type: 'status' }
        ];
        changeMultiSearchOption(ualist);
        break;
      case 'unusedInactiveAp':
        const uialist = [
          { value: licenseConfig.usedTypes.unused, type: 'licenseType' },
          { value: licenseConfig.statusTypes.inactive, type: 'status' },
          { value: deviceType.ap, type: 'licenseDeviceType' }
        ];
        changeMultiSearchOption(uialist);
        break;
      case 'unusedInactiveSwitch':
        const uislist = [
          { value: licenseConfig.usedTypes.unused, type: 'licenseType' },
          { value: licenseConfig.statusTypes.inactive, type: 'status' },
          { value: deviceType.switch, type: 'licenseDeviceType' }
        ];
        changeMultiSearchOption(uislist);
        break;
      case 'unusedInactiveGateway':
        const uiglist = [
          { value: licenseConfig.usedTypes.unused, type: 'licenseType' },
          { value: licenseConfig.statusTypes.inactive, type: 'status' },
          { value: deviceType.gateway, type: 'licenseDeviceType' }
        ];
        changeMultiSearchOption(uiglist);
        break;
      default: // =total
        return;
    }
  };

  useEffect(() => {
    handleSearch(search);
  }, [search]);

  useEffect(() => {
    if (!isNil(quickSearchKey)) {
      initOrgSearch();
      inputQuickSearch(quickSearchKey);
    }
  }, [quickSearchKey]);

  return (
    <InlineTitle isNonUnderline>
      <Button label={t('368dec020c')} className="btn-grey" onClick={() => { navigate('/settings/license-history'); }} />
      <DropdownWithAdvancedSearch
        value={search.searchKey}
        alignEnd={true}
        dataBsToggleOnButton={true}
        dropdownMenuStyle={{ minWidth: 371 }}
        onChange={(e) => { setSearch({ ...search, searchKey: e.target.value }); }}
        onClick={() => { initOrgSearch(); }}
        buttonOnClick={e => {
          disableFuzzySearch();
        }}
      >
        <li>
          <div className="form-title">{`${t('5dddd8b61e')} :`}</div>
          <DropdownWithItem
            id="status-dropdown"
            type="normal"
            selectedItem={getSelectedFilter('licenseType')}
            itemList={getFilterList('licenseType')}
            onClick={(e) => { changeSearchOption(e, 'licenseType'); }}
          />
        </li>
        <li>
          <div className="form-title">{`${t('ec53a8c4f0')} :`}</div>
          <DropdownWithItem
            id="status-dropdown"
            type="normal"
            selectedItem={getSelectedFilter('status')}
            itemList={getFilterList('status')}
            onClick={(e) => { changeSearchOption(e, 'status'); }}
          />
        </li>
        <li>
          <div className="form-title">{`${t('768d0c0b0b')} :`}</div>
          <DropdownWithItem
            id="status-dropdown"
            type="normal"
            selectedItem={getSelectedFilter('licenseDeviceType')}
            itemList={getFilterList('licenseDeviceType')}
            onClick={(e) => { changeSearchOption(e, 'licenseDeviceType'); }}
          />
        </li>
        <li>
          <div className="form-title">{`${t('5d5c4ad5db')} :`}</div>
          <Input
            type="text"
            autoComplete="new-email1"
            onChange={e => {
              changeSearchOption(e, 'licenseKey');
            }}
            onClick={(e) => { initSearchKey(); }}
            onFocus={() => { }}
            onBlur={() => { }}
            value={search?.searchFields?.licenseKey?.value}
          />
        </li>
        <li>
          <div className="form-title">{`${t('4d29676394')} :`}</div>
          <DropdownWithItem
            id="status-dropdown"
            type="normal"
            selectedItem={getSelectedFilter('timeRemaining')}
            itemList={getFilterList('timeRemaining')}
            onClick={(e) => { changeSearchOption(e, 'timeRemaining'); }}
          />
        </li>
      </DropdownWithAdvancedSearch>

      <Button
        label=""
        title={t('d6126a628c')}
        className="icon-download"
        style={{ border: 'none', height: 25, backgroundColor: '#fff' }}
        onClick={() => downloadORGCsv()}
      />
    </InlineTitle>
  );
};

export default LicensesToolbarBoxORG;